import { formatAnchorLabel } from '@lib/media/commentAnchor';
import { authorLabel, isOwnWriting } from '@lib/media/commentAuthor';
import { formatTimeAgoOrDate } from '@lib/utils/relativeTime';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import type { MediaComment, MediaCommentReply } from '@stores/MediaComments';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { CommentComposer } from './CommentComposer';
import css from './CommentsPanel.module.scss';

/**
 * Every write a thread can ask for. Each resolves to whether it was accepted, and each has
 * already told the reader when it was not — see `CommentsPanel`'s `report`. Nothing here
 * closes an editor or a confirmation on its own: the boolean is what the caller closes on, so
 * a refused write leaves the reader looking at what they were trying to do.
 */
export type ThreadActions = {
  edit: (commentId: string, body: string) => Promise<boolean>;
  remove: (commentId: string) => Promise<boolean>;
  reply: (commentId: string, body: string) => Promise<boolean>;
  editReply: (commentId: string, replyId: string, body: string) => Promise<boolean>;
  removeReply: (commentId: string, replyId: string) => Promise<boolean>;
};

type EntryProps = {
  entry: MediaCommentReply;
  /** Null while the session is still loading — nothing is claimed as the reader's until it is known. */
  currentUserId: string | null;
  /** False in a read-only library: the API refuses the writes, so the controls are not offered. */
  canWrite: boolean;
  /** The anchor chip, on a comment. A reply has no anchor of its own. */
  anchorSlot?: ReactNode;
  /**
   * The controls first in the row under the text, before Edit and Delete — Reply on whichever
   * entry ends the thread, and Copy link on the comment. Everyone gets these; Edit and Delete are
   * the author's alone.
   */
  leadingActions?: ReactNode;
  onEdit: (body: string) => Promise<boolean>;
  onDelete: () => Promise<boolean>;
  /** Named in the delete confirmation when deleting takes replies with it. */
  cascadeWarning?: string;
};

/**
 * One piece of writing — a comment or a reply. The two differ only in what hangs off them (an
 * anchor on the comment, the Reply control on whichever one ends the thread), so they share
 * this rather than a near-copy each.
 *
 * Edit and Delete appear only on your own writing, matching the API, which refuses both on
 * anyone else's. Revealing them on hover is `.entryActions`' job, including the `no-hover`
 * case — a tablet renders this desktop tree with no cursor to reveal them with.
 */
const CommentEntry = ({
  entry,
  currentUserId,
  canWrite,
  anchorSlot,
  leadingActions,
  onEdit,
  onDelete,
  cascadeWarning,
}: EntryProps) => {
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { author } = entry;
  const isMine = isOwnWriting(author, currentUserId);
  const hasActions = !!leadingActions || (isMine && canWrite);
  const edited = entry.updatedAt > entry.createdAt;

  // The avatar, the name and the anchor stay put while the text is being edited; only the body
  // below them swaps for the composer. Replacing the whole entry — which this did — left a bare
  // box under the comment that reads exactly like a new reply being drafted, with nothing
  // saying whose writing it is or that it already exists.
  return (
    <div className={css.entry}>
      <span className={css.avatar} style={{ backgroundColor: author.avatarColor }} aria-hidden>
        {author.initial}
      </span>
      <div className={css.entryBody}>
        <div className={css.entryMeta}>
          <span className={css.authorName}>{authorLabel(author, currentUserId)}</span>
          {anchorSlot}
          {editing ? (
            <span className={css.editingHint}>Editing</span>
          ) : (
            <span className={css.entryTime}>
              {formatTimeAgoOrDate(entry.createdAt)}
              {edited && ' · edited'}
            </span>
          )}
        </div>

        {editing ? (
          <CommentComposer
            // Seen only if the writer clears the field — it opens pre-filled. Generic because
            // this same entry renders a reply, which is not a comment.
            placeholder="Edit your note…"
            submitLabel="Save"
            initialValue={entry.body}
            autoFocus
            onCancel={() => setEditing(false)}
            onSubmit={async (body) => {
              const saved = await onEdit(body);
              if (saved) setEditing(false);
              return saved;
            }}
          />
        ) : (
          <>
            {/* `pre-wrap`, so the paragraphs someone typed survive. The body is displayed as
                written and never interpreted — no markdown, no links. */}
            <p className={css.entryText}>{entry.body}</p>

            {/* Under the text, on a row of their own — but a short one. Hover-revealed actions
                keep their space while hidden (that is what stops the list jumping as the pointer
                crosses it), so the row is 14px rather than a full text line, and the thread's own
                padding is trimmed to pay for it. See `.entryActions`.

                Withdrawn while the entry's delete is being confirmed: that state has its own
                buttons in the same place. */}
            {hasActions && !confirmingDelete && (
              <div className={css.entryActions}>
                {leadingActions}
                {isMine && canWrite && (
                  <>
                    <button
                      type="button"
                      className={css.linkButton}
                      onClick={() => setEditing(true)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={css.linkButton}
                      onClick={() => setConfirmingDelete(true)}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            )}

            {confirmingDelete && (
              <div className={css.confirmRow}>
                <span className={css.confirmText}>{cascadeWarning ?? 'Delete this?'}</span>
                <button
                  type="button"
                  className={css.dangerButton}
                  onClick={async () => {
                    // Left open on failure: the toast explains why, and the row it names is still
                    // the one under the reader's cursor.
                    if (await onDelete()) setConfirmingDelete(false);
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className={css.secondaryButton}
                  onClick={() => setConfirmingDelete(false)}
                >
                  Keep
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

type Props = {
  comment: MediaComment;
  /** Null while the session is still loading — nothing is claimed as the reader's until it is known. */
  currentUserId: string | null;
  canWrite: boolean;
  /** The comment last gone to — from its chip or a marker — until another one is. */
  isSelected: boolean;
  /**
   * Takes the player to the comment. A span-anchored comment also hands its span over as the
   * marked range where the host accepts one — the same offer the Scenes panel makes, and for
   * the same reason: someone has already worked out where this bit starts and ends.
   */
  onGoTo: () => void;
  onCopyLink: () => void;
  actions: ThreadActions;
};

export const CommentThread = ({
  comment,
  currentUserId,
  canWrite,
  isSelected,
  onGoTo,
  onCopyLink,
  actions,
}: Props) => {
  const [replying, setReplying] = useState(false);

  const replyCount = comment.replies.length;

  /**
   * Hung off whichever entry ends the thread — the comment while nobody has answered, the last
   * reply once someone has — because the box it opens always appears at the end of the thread.
   * On the comment alone, with ten replies under it, the control and the box it opened were ten
   * replies apart.
   *
   * On the last entry only, never on every reply: a Reply on each one would promise answering
   * that reply in particular, and threads are one level deep — every reply answers the comment.
   *
   * Rendered whether or not the box is open, and disabled rather than removed while it is.
   * Dropping it shifted Edit and Delete left into the space it left behind — directly under the
   * cursor that had just pressed Reply, with Delete one word further along.
   */
  const replyControl = canWrite ? (
    <button
      type="button"
      className={css.linkButton}
      disabled={replying}
      onClick={() => setReplying(true)}
    >
      Reply
    </button>
  ) : null;

  return (
    <article
      data-comment-id={comment.id}
      className={classNames(css.thread, { [css.threadSelected]: isSelected })}
    >
      <CommentEntry
        entry={comment}
        currentUserId={currentUserId}
        canWrite={canWrite}
        anchorSlot={
          <button type="button" className={css.anchorChip} onClick={onGoTo}>
            <AccessTimeRoundedIcon className={css.anchorChipIcon} />
            {formatAnchorLabel(comment.anchor)}
          </button>
        }
        leadingActions={
          <>
            {replyCount === 0 && replyControl}
            {/* On the comment only. A reply has no anchor of its own to link to, and its thread's
                link already opens on it — the comment and every reply under it. */}
            <button type="button" className={css.linkButton} onClick={onCopyLink}>
              Copy link
            </button>
          </>
        }
        onEdit={(body) => actions.edit(comment.id, body)}
        onDelete={() => actions.remove(comment.id)}
        cascadeWarning={
          replyCount > 0
            ? `Delete this comment and its ${replyCount === 1 ? 'reply' : `${replyCount} replies`}?`
            : 'Delete this comment?'
        }
      />

      {/* The replies and the box for writing one share a container, so the rule down the left
          of the thread is one line. Two containers each drew their own, leaving a visible gap
          between the last reply and the composer. */}
      {(replyCount > 0 || replying) && (
        <div className={css.replies}>
          {comment.replies.map((reply, index) => (
            <CommentEntry
              key={reply.id}
              entry={reply}
              currentUserId={currentUserId}
              canWrite={canWrite}
              leadingActions={index === replyCount - 1 ? replyControl : undefined}
              onEdit={(body) => actions.editReply(comment.id, reply.id, body)}
              onDelete={() => actions.removeReply(comment.id, reply.id)}
              cascadeWarning="Delete this reply?"
            />
          ))}
          {replying && (
            <CommentComposer
              placeholder="Write a reply…"
              submitLabel="Reply"
              autoFocus
              onCancel={() => setReplying(false)}
              onSubmit={async (body) => {
                const posted = await actions.reply(comment.id, body);
                if (posted) setReplying(false);
                return posted;
              }}
            />
          )}
        </div>
      )}
    </article>
  );
};

/** Where a thread sits in the list, for the panel's scroll-into-view. */
export const threadSelector = (commentId: string) => `[data-comment-id="${CSS.escape(commentId)}"]`;
